/**
 * Hand-built SVG charts sized to their container, drawn in the app's palette.
 * Each carries a text alternative; values are never encoded by colour alone.
 */
import { motion, useReducedMotion } from 'motion/react';
import { useId, useState } from 'react';
import { date } from '../lib/format.ts';
import { EASE } from './motion.tsx';

export function PulseBars({ data, height = 168 }: { data: { date: string; present: number; late: number; scheduled: number }[]; height?: number }) {
  const reduce = useReducedMotion();
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...data.map((d) => d.scheduled));
  const w = 100 / Math.max(data.length, 1);
  const summary = data.length ? `Attendance over ${data.length} days; latest ${data.at(-1)!.present} present of ${data.at(-1)!.scheduled} scheduled, ${data.at(-1)!.late} late.` : 'No attendance data';
  return (
    <figure style={{ margin: 0 }}>
      <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" width="100%" height={height} role="img" aria-label={summary} onMouseLeave={() => setHover(null)}>
        {[0.25, 0.5, 0.75, 1].map((g) => (
          <line key={g} x1="0" x2="100" y1={height - 22 - (height - 34) * g} y2={height - 22 - (height - 34) * g} stroke="var(--line-1)" strokeWidth="0.3" vectorEffect="non-scaling-stroke" />
        ))}
        {data.map((d, i) => {
          const h = ((height - 34) * d.present) / max;
          const sh = ((height - 34) * d.scheduled) / max;
          const x = i * w + w * 0.2;
          const bw = w * 0.6;
          const isWeekend = [0, 6].includes(new Date(`${d.date}T00:00:00Z`).getUTCDay());
          return (
            <g key={d.date} onMouseEnter={() => setHover(i)}>
              <rect x={i * w} y="0" width={w} height={height} fill="transparent" />
              <rect x={x} y={height - 22 - sh} width={bw} height={sh} rx="0.6" fill="var(--bg-4)" opacity={isWeekend ? 0.5 : 1} />
              <motion.rect
                x={x}
                width={bw}
                rx="0.6"
                fill={hover === i ? 'var(--accent)' : 'var(--ok)'}
                initial={reduce ? { y: height - 22 - h, height: h } : { y: height - 22, height: 0 }}
                animate={{ y: height - 22 - h, height: h }}
                transition={{ duration: 0.9, ease: EASE, delay: reduce ? 0 : 0.2 + i * 0.03 }}
              />
            </g>
          );
        })}
        <motion.path
          d={data.map((d, i) => `${i ? 'L' : 'M'}${i * w + w / 2},${height - 22 - ((height - 34) * d.late * 4) / max}`).join(' ')}
          fill="none"
          stroke="var(--clay)"
          strokeWidth="1.4"
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
          initial={reduce ? false : { pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.4, ease: EASE, delay: 0.5 }}
        />
      </svg>
      <div className="row row--between" style={{ marginTop: 6 }}>
        <span className="eyebrow">{data[0] ? date(data[0].date, { year: false }) : ''}</span>
        <span className="eyebrow" aria-live="polite">
          {hover !== null && data[hover] ? `${date(data[hover]!.date, { weekday: true, year: false })} · ${data[hover]!.present}/${data[hover]!.scheduled} in · ${data[hover]!.late} late` : (
            <>
              <span style={{ color: 'var(--ok)' }}>■</span> present &nbsp; <span style={{ color: 'var(--clay)' }}>—</span> late ×4
            </>
          )}
        </span>
        <span className="eyebrow">{data.at(-1) ? date(data.at(-1)!.date, { year: false }) : ''}</span>
      </div>
    </figure>
  );
}

export function Ring({ value, max, size = 132, stroke = 9, label, sublabel }: { value: number; max: number; size?: number; stroke?: number; label: string; sublabel?: string }) {
  const reduce = useReducedMotion();
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  return (
    <div style={{ position: 'relative', width: size, height: size }} role="img" aria-label={`${label}: ${value} of ${max}`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bg-4)" strokeWidth={stroke} />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--accent)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          initial={reduce ? { strokeDashoffset: c * (1 - pct) } : { strokeDashoffset: c }}
          animate={{ strokeDashoffset: c * (1 - pct) }}
          transition={{ duration: 1.3, ease: EASE, delay: 0.2 }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
        <div>
          <div className="display" style={{ fontSize: size * 0.32 }}>
            {value}
          </div>
          {sublabel ? <div className="eyebrow" style={{ marginTop: 2 }}>{sublabel}</div> : null}
        </div>
      </div>
    </div>
  );
}

/** Horizontal composition bar (e.g. where the payroll money goes). */
export function CompositionBar({ parts }: { parts: { label: string; value: number; tone: string }[] }) {
  const total = parts.reduce((a, p) => a + Math.max(0, p.value), 0) || 1;
  const id = useId();
  return (
    <div>
      <div style={{ display: 'flex', height: 10, borderRadius: 6, overflow: 'hidden', gap: 2 }} role="img" aria-labelledby={id}>
        {parts.map((p, i) => (
          <motion.span key={p.label} style={{ background: p.tone, display: 'block' }} initial={{ flexGrow: 0 }} animate={{ flexGrow: Math.max(0, p.value) / total }} transition={{ duration: 1, ease: EASE, delay: 0.1 + i * 0.06 }} />
        ))}
      </div>
      <ul id={id} style={{ listStyle: 'none', margin: '14px 0 0', padding: 0, display: 'flex', flexWrap: 'wrap', gap: '8px 18px' }}>
        {parts.map((p) => (
          <li key={p.label} className="row" style={{ ['--gap' as string]: '8px', fontSize: 'var(--step--1)' }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: p.tone }} />
            <span className="muted">{p.label}</span>
            <span className="num">{Math.round((Math.max(0, p.value) / total) * 100)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TrendLine({ points, height = 90 }: { points: { label: string; value: number }[]; height?: number }) {
  const reduce = useReducedMotion();
  if (points.length < 2) return null;
  const max = Math.max(...points.map((p) => p.value));
  const min = Math.min(...points.map((p) => p.value)) * 0.9;
  const xy = points.map((p, i) => [(i / (points.length - 1)) * 100, height - 8 - ((p.value - min) / (max - min || 1)) * (height - 18)] as const);
  const d = xy.map(([x, y], i) => `${i ? 'L' : 'M'}${x},${y}`).join(' ');
  return (
    <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" width="100%" height={height} role="img" aria-label={`Trend from ${points[0]!.label} to ${points.at(-1)!.label}`}>
      <motion.path d={`${d} L100,${height} L0,${height} Z`} fill="var(--accent-soft)" initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.6 }} />
      <motion.path d={d} fill="none" stroke="var(--accent-text)" strokeWidth="1.6" vectorEffect="non-scaling-stroke" initial={reduce ? false : { pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.3, ease: EASE }} />
      {xy.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="1.2" fill="var(--bg-2)" stroke="var(--accent-text)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      ))}
    </svg>
  );
}
